import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  App,
  Button,
  Card,
  Col,
  DatePicker,
  Empty,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
} from "antd";
import type { TableColumnsType, TablePaginationConfig } from "antd";
import {
  BarChartOutlined,
  BellOutlined,
  CalendarOutlined,
  CloudServerOutlined,
  DisconnectOutlined,
  InfoCircleOutlined,
  ReloadOutlined,
  SafetyCertificateOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import type { Dayjs } from "dayjs";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import {
  getDevicePackageDefinition,
  type DevicePackageKey,
} from "../constants/devicePackages";
import { useDedupedMessage } from "../hooks/useDedupedMessage";
import { usePermissions } from "../hooks/usePermissions";
import { localeService } from "../services/localeService";
import { getHomeOverview } from "../services/homeService";
import { getStatisticsData } from "../services/statisticsService";
import type {
  HomeLureReminder,
  HomeNetworkRenewalContext,
  HomeOverviewResponse,
  HomePackageDevice,
  HomeRenewalDevice,
  HomeRenewalStatus,
} from "../types/home";
import type {
  StatisticsAvailableDateRange,
  StatisticsSummaryDataType,
} from "../types/statistics";
import {
  getDevicePackageDisplayName,
} from "../utils/devicePackageDisplay";
import "./FeatureAwareHome.css";

const { Paragraph, Text, Title } = Typography;
const { RangePicker } = DatePicker;

const PREVIEW_DEVICE_COUNT = 5;
const PACKAGE_PREVIEW_COUNT = 3;
const DEFAULT_MODAL_PAGE_SIZE = 10;
const MODAL_PAGE_SIZE_OPTIONS = ["10", "20", "50", "100"];
const DETAIL_MODAL_TABLE_SCROLL_Y = 320;
const RENEWAL_MODAL_TABLE_SCROLL_Y = 420;
const HOME_TREND_CHART_WIDTH = 980;
const HOME_TREND_CHART_HEIGHT = 300;
const HOME_TREND_CHART_PADDING_TOP = 26;
const HOME_TREND_CHART_PADDING_RIGHT = 18;
const HOME_TREND_CHART_PADDING_BOTTOM = 42;
const HOME_TREND_CHART_PADDING_LEFT = 48;
const HOME_TREND_CHART_Y_TICK_COUNT = 5;
const HOME_TREND_CHART_X_LABEL_COUNT = 7;
const EMPTY_HOME_TREND_DATA: Array<{ date: string; count: number }> = [];
const HOME_MONITORING_ANALYSIS_FEATURE = "mosquito_count_monitoring";
const EMPTY_NETWORK_RENEWAL_CONTEXT: HomeNetworkRenewalContext = {
  view_type: "project",
  scope_name: "",
  current_project_name: "",
  expiring_count: 0,
  overdue_count: 0,
  projects: [],
  devices: [],
  page: 1,
  pageSize: DEFAULT_MODAL_PAGE_SIZE,
  total: 0,
  items: [],
};

type HomeTrendRangeReason =
  | "firstDeviceCreatedAt"
  | "empty"
  | "custom";

interface HomeTrendRange {
  start: Dayjs;
  end: Dayjs;
  reason: HomeTrendRangeReason;
}

interface HomeRecognitionDashboardState {
  trendSummary: StatisticsSummaryDataType | null;
  range: HomeTrendRange;
  loading: boolean;
}

interface HomeTrendChartPoint {
  date: string;
  count: number;
  x: number;
  y: number;
}

const listPreviewStyle: React.CSSProperties = {
  paddingRight: 4,
};

const cardListItemStyle: React.CSSProperties = {
  borderBottom: "1px solid #f0f0f0",
  padding: "12px 0",
};

const headerLinkButtonStyle: React.CSSProperties = {
  paddingInline: 0,
};

const modalBodyStyle: React.CSSProperties = {
  maxHeight: 640,
  overflowY: "auto",
  overflowX: "hidden",
};

const detailModalBodyStyle: React.CSSProperties = {
  ...modalBodyStyle,
  overflowY: "hidden",
};

const wideModalWidth = "min(1440px, calc(100vw - 48px))";

const detailFilterToolbarStyle: React.CSSProperties = {
  alignItems: "center",
  display: "flex",
  flexWrap: "wrap",
  gap: 12,
  width: "100%",
};

const detailFilterSearchStyle: React.CSSProperties = {
  flex: "1 1 360px",
  minWidth: 260,
};

const detailFilterSelectStyle: React.CSSProperties = {
  flex: "0 1 320px",
  minWidth: 220,
};

const detailFilterActionsStyle: React.CSSProperties = {
  alignItems: "center",
  display: "flex",
  flex: "0 0 auto",
  gap: 12,
  marginLeft: "auto",
};

const tableTextWrapStyle: React.CSSProperties = {
  display: "inline-block",
  maxWidth: "100%",
  whiteSpace: "normal",
  wordBreak: "break-word",
};

const dashboardCardStyle: React.CSSProperties = {
  borderRadius: 8,
  boxShadow: "0 3px 12px rgba(15, 23, 42, 0.06)",
  height: "100%",
};

const dashboardCardBodyStyle: React.CSSProperties = {
  minHeight: 420,
  padding: "22px 24px",
};

const cardHeaderBadgeRowStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "flex-start",
  transform: "translateY(-4px)",
  width: "100%",
};

const summaryShellStyle: React.CSSProperties = {
  background: "#fbfdff",
  border: "1px solid #d7ecff",
  borderRadius: 8,
  boxShadow: "0 8px 24px rgba(24, 144, 255, 0.08)",
  padding: 24,
};

const serviceOverviewStyle: React.CSSProperties = {
  background: "#fff",
  border: "1px solid #e6f1fb",
  borderRadius: 8,
  padding: 18,
};

const summaryPanelBaseStyle: React.CSSProperties = {
  borderRadius: 8,
  minHeight: 96,
  padding: "18px 22px",
};

const summaryPanelStyles: Record<
  "offline" | "lure" | "network",
  React.CSSProperties
> = {
  offline: {
    background: "#fff8ef",
    border: "1px solid #ffd8a8",
  },
  lure: {
    background: "#f0f6ff",
    border: "1px solid #bfd7ff",
  },
  network: {
    background: "#fffbe8",
    border: "1px solid #f5df8a",
  },
};

const summaryValueStyles: Record<
  "offline" | "lure" | "network",
  React.CSSProperties
> = {
  offline: { color: "#d97706" },
  lure: { color: "#2563eb" },
  network: { color: "#d48806" },
};

const getRenewalStatusColor = (status: HomeRenewalStatus) => {
  if (status === "overdue") {
    return "red";
  }
  if (status === "expiring") {
    return "orange";
  }
  return "green";
};

const createEmptyHomeTrendRange = (
  reason: HomeTrendRangeReason = "empty",
  businessDate?: string | null,
): HomeTrendRange => {
  const end = dayjs(businessDate || undefined).endOf("day");

  return {
    start: end.startOf("day"),
    end,
    reason,
  };
};

const resolveHomeTrendRangeFromAvailability = (
  availableDateRange: StatisticsAvailableDateRange | null | undefined,
  businessDate?: string | null,
): HomeTrendRange => {
  if (
    availableDateRange?.startDate &&
    availableDateRange.endDate
  ) {
    return {
      start: dayjs(availableDateRange.startDate).startOf("day"),
      end: dayjs(availableDateRange.endDate).endOf("day"),
      reason: "firstDeviceCreatedAt",
    };
  }

  if (
    availableDateRange &&
    Number.isFinite(availableDateRange.startTime) &&
    Number.isFinite(availableDateRange.endTime) &&
    availableDateRange.startTime <= availableDateRange.endTime
  ) {
    const parsedStart = dayjs(availableDateRange.startTime);
    const parsedEnd = dayjs(availableDateRange.endTime);
    if (parsedStart.isValid() && parsedEnd.isValid()) {
      return {
        start: parsedStart.startOf("day"),
        end: parsedEnd.endOf("day"),
        reason: "firstDeviceCreatedAt",
      };
    }
  }

  return createEmptyHomeTrendRange("empty", businessDate);
};

const formatHomeTrendQueryRange = (
  range: HomeTrendRange,
): [string, string] => [
  range.start.format("YYYY-MM-DD"),
  range.end.format("YYYY-MM-DD"),
];

const formatDashboardNumber = (value: number | null | undefined) =>
  typeof value === "number" && Number.isFinite(value)
    ? value.toLocaleString()
    : "-";

const buildTrendChartPoints = (
  trendData: Array<{ date: string; count: number }>,
) => {
  if (trendData.length === 0) {
    return [];
  }

  const innerWidth =
    HOME_TREND_CHART_WIDTH -
    HOME_TREND_CHART_PADDING_LEFT -
    HOME_TREND_CHART_PADDING_RIGHT;
  const innerHeight =
    HOME_TREND_CHART_HEIGHT -
    HOME_TREND_CHART_PADDING_TOP -
    HOME_TREND_CHART_PADDING_BOTTOM;
  const maxCount = getHomeTrendMaxCount(trendData);

  return trendData.map((item, index) => {
    const x =
      trendData.length === 1
        ? HOME_TREND_CHART_WIDTH / 2
        : HOME_TREND_CHART_PADDING_LEFT +
          (innerWidth * index) / Math.max(1, trendData.length - 1);
    const y =
      HOME_TREND_CHART_PADDING_TOP +
      innerHeight -
      (Math.max(0, item.count) / maxCount) * innerHeight;

    return {
      ...item,
      x,
      y,
    };
  });
};

const getHomeTrendMaxCount = (
  trendData: Array<{ date: string; count: number }>,
) => {
  const rawMaxCount = Math.max(0, ...trendData.map((item) => item.count));
  return Math.max(4, Math.ceil(rawMaxCount / 4) * 4);
};

const formatHomeTrendAxisDate = (value: string) => {
  const parsedDate = dayjs(value);
  return parsedDate.isValid() ? parsedDate.format("MM/DD") : value;
};

const buildHomeTrendSeries = (
  range: HomeTrendRange,
  trendData: Array<{ date: string; count: number }>,
) => {
  const countsByDate = new Map(
    trendData.map((item) => [
      dayjs(item.date).isValid()
        ? dayjs(item.date).format("YYYY-MM-DD")
        : item.date,
      item.count,
    ]),
  );
  const start = range.start.startOf("day");
  const end = range.end.startOf("day");
  const days = Math.max(1, end.diff(start, "day") + 1);

  return Array.from({ length: days }, (_, index) => {
    const date = start.add(index, "day").format("YYYY-MM-DD");
    return {
      date,
      count: countsByDate.get(date) ?? 0,
    };
  });
};

const normalizePackageStyleKey = (
  packageKey: string | null | undefined,
): DevicePackageKey => getDevicePackageDefinition(packageKey).key;

const getPackageTagColor = (packageKey: string | null | undefined) => {
  const normalized = normalizePackageStyleKey(packageKey);
  if (normalized === "insight") {
    return "volcano";
  }
  if (normalized === "recognition") {
    return "blue";
  }
  return "cyan";
};

const ALL_PROJECT_LABELS = new Set(["全部项目", "全部項目"]);
const NO_AUTHORIZED_PROJECT_LABELS = new Set([
  "暂无授权项目",
  "暫無授權項目",
]);
const AUTHORIZED_PROJECT_LABEL_PATTERN =
  /^(?:已授权项目|已授權項目)[（(](\d+)(?:个|個)[）)]$/;
const PROJECT_WITH_CHILDREN_LABEL_PATTERN =
  /^(.+)[（(]含(\d+)(?:个|個)子项目[）)]$/;

const FeatureAwareHome: React.FC = () => {
  const { message: rawMessage } = App.useApp();
  const message = useDedupedMessage(rawMessage);
  const navigate = useNavigate();
  const {
    canAccessPackageManagement,
    canAccessStatistics,
    canReadDevices,
    canManageProjects,
    isPlatformAccount,
  } = usePermissions();
  const { t } = useTranslation(["home", "device", "common"]);

  const [overview, setOverview] = useState<HomeOverviewResponse | null>(null);
  const [renewalContext] =
    useState<HomeNetworkRenewalContext>(EMPTY_NETWORK_RENEWAL_CONTEXT);
  const [, setLoading] = useState(true);
  const [networkDetailOpen, setNetworkDetailOpen] = useState(false);
  const [renewalOpen, setRenewalOpen] = useState(false);
  const [selectedRenewalDeviceIds, setSelectedRenewalDeviceIds] = useState<
    string[]
  >([]);
  const [packageDetailOpen, setPackageDetailOpen] = useState(false);
  const [renewalKeyword, setRenewalKeyword] = useState("");
  const [renewalStatusFilter, setRenewalStatusFilter] = useState<
    HomeRenewalStatus | "all"
  >("all");
  const [packageKeyword, setPackageKeyword] = useState("");
  const [currentPackageFilter, setCurrentPackageFilter] = useState<string>("all");
  const [targetPackageFilter, setTargetPackageFilter] = useState<string>("all");
  const [networkPage, setNetworkPage] = useState(1);
  const [networkPageSize, setNetworkPageSize] = useState(DEFAULT_MODAL_PAGE_SIZE);
  const [packagePage, setPackagePage] = useState(1);
  const [packagePageSize, setPackagePageSize] = useState(DEFAULT_MODAL_PAGE_SIZE);
  const [recognitionDashboard, setRecognitionDashboard] =
    useState<HomeRecognitionDashboardState>(() => ({
      trendSummary: null,
      range: createEmptyHomeTrendRange(),
      loading: true,
    }));
  const [recognitionHoveredPoint, setRecognitionHoveredPoint] =
    useState<HomeTrendChartPoint | null>(null);
  const recognitionDashboardRequestRef = useRef(0);

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const nextOverview = await getHomeOverview({
        page: packagePage,
        pageSize: packagePageSize,
        keyword: packageKeyword.trim() || undefined,
        currentPackage: currentPackageFilter,
        targetPackage: targetPackageFilter,
      });
      setOverview(nextOverview);
    } finally {
      setLoading(false);
    }
  }, [
    currentPackageFilter,
    packageKeyword,
    packagePage,
    packagePageSize,
    targetPackageFilter,
  ]);

  useEffect(() => {
    void loadDashboard();
  }, [loadDashboard]);

  const loadRecognitionDashboard = useCallback(async (rangeOverride?: HomeTrendRange) => {
    if (!canAccessStatistics) {
      setRecognitionDashboard((current) => ({
        ...current,
        trendSummary: null,
        loading: false,
      }));
      return;
    }

    const requestId = recognitionDashboardRequestRef.current + 1;
    recognitionDashboardRequestRef.current = requestId;
    setRecognitionDashboard((current) => ({
      ...current,
      range: rangeOverride ?? current.range,
      loading: true,
    }));

    let range: HomeTrendRange;
    if (rangeOverride) {
      range = rangeOverride;
    } else {
      const [rangeResult] = await Promise.allSettled([
        getStatisticsData(
          undefined,
          undefined,
          undefined,
          HOME_MONITORING_ANALYSIS_FEATURE,
        ),
      ]);
      const statisticsResponse =
        rangeResult.status === "fulfilled" ? rangeResult.value : null;
      range = resolveHomeTrendRangeFromAvailability(
        statisticsResponse?.availableDateRange,
        statisticsResponse?.businessDate,
      );
    }

    const [startDate, endDate] = formatHomeTrendQueryRange(range);
    const [trendResult] = await Promise.allSettled([
      getStatisticsData(
        startDate,
        endDate,
        undefined,
        HOME_MONITORING_ANALYSIS_FEATURE,
      ),
    ]);
    const trendResponse =
      trendResult.status === "fulfilled" ? trendResult.value : null;

    if (recognitionDashboardRequestRef.current !== requestId) {
      return;
    }

    setRecognitionDashboard({
      trendSummary: trendResponse?.summary ?? null,
      range,
      loading: false,
    });
  }, [
    canAccessStatistics,
  ]);

  useEffect(() => {
    void loadRecognitionDashboard();
  }, [loadRecognitionDashboard]);

  const handleRecognitionRangeChange = useCallback(
    (dates: null | [Dayjs | null, Dayjs | null]) => {
      if (!dates?.[0] || !dates[1]) {
        return;
      }

      void loadRecognitionDashboard({
        start: dates[0].startOf("day"),
        end: dates[1].endOf("day"),
        reason: "custom",
      });
    },
    [loadRecognitionDashboard],
  );

  const formatDateTime = useCallback(
    (value: string | null | undefined) =>
      localeService.formatDate(value, {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      }),
    [],
  );

  const formatDateOnly = useCallback(
    (value: string | null | undefined) =>
      localeService.formatDate(value, {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }),
    [],
  );
  const formatCurrency = useCallback(
    (amount: number) => localeService.formatCurrency(amount),
    [],
  );

  const packageLabel = useCallback(
    (packageKey: string | null | undefined) => {
      const packageSummary = overview?.package_distribution.find(
        (item) => item.package_key === packageKey,
      );
      return getDevicePackageDisplayName(packageKey, packageSummary?.name, t);
    },
    [overview, t],
  );

  const networkDueDevices = useMemo(
    () =>
      (renewalContext?.devices ?? []).filter(
        (device) => device.status === "expiring" || device.status === "overdue",
      ),
    [renewalContext],
  );
  const currentScopeName =
    renewalContext?.scope_name ||
    overview?.scope_name ||
    overview?.project_name ||
    "-";
  const currentProjectName =
    renewalContext?.current_project_name ||
    overview?.project_name ||
    currentScopeName;
  const localizeProjectScopeName = useCallback(
    (name: string) => {
      if (ALL_PROJECT_LABELS.has(name)) {
        return t("home:dashboard.scopeLabels.allProjects");
      }
      if (NO_AUTHORIZED_PROJECT_LABELS.has(name)) {
        return t("home:dashboard.scopeLabels.noAuthorizedProjects");
      }

      const authorizedProjectMatch = name.match(AUTHORIZED_PROJECT_LABEL_PATTERN);
      if (authorizedProjectMatch) {
        return t("home:dashboard.scopeLabels.authorizedProjects", {
          count: Number(authorizedProjectMatch[1]),
        });
      }

      const projectWithChildrenMatch = name.match(
        PROJECT_WITH_CHILDREN_LABEL_PATTERN,
      );
      if (projectWithChildrenMatch) {
        return t("home:dashboard.scopeLabels.projectWithChildren", {
          project: projectWithChildrenMatch[1],
          count: Number(projectWithChildrenMatch[2]),
        });
      }

      return name;
    },
    [t],
  );
  const localizedCurrentScopeName = localizeProjectScopeName(currentScopeName);
  const localizedCurrentProjectName = localizeProjectScopeName(currentProjectName);

  const renewalFilteredDevices = useMemo(
    () => renewalContext?.items ?? [],
    [renewalContext?.items],
  );

  useEffect(() => {
    if (!renewalOpen) {
      return;
    }

    setSelectedRenewalDeviceIds((current) =>
      current.filter((deviceId) =>
        renewalFilteredDevices.some((device) => device.id === deviceId),
      ),
    );
  }, [renewalFilteredDevices, renewalOpen]);

  const resetRenewalControls = () => {
    setSelectedRenewalDeviceIds([]);
    setRenewalKeyword("");
    setRenewalStatusFilter("all");
    setNetworkPage(1);
    setNetworkPageSize(DEFAULT_MODAL_PAGE_SIZE);
  };

  const refreshRenewalData = () => {
    void loadDashboard();
  };

  const closeRenewalModal = () => {
    setRenewalOpen(false);
    resetRenewalControls();
  };

  const packageDetailDevices = overview?.items ?? [];

  const upgradableDeviceCount =
    overview?.package_devices.filter((device) => device.upgrade_to !== null)
      .length ?? 0;
  const highestPackageDeviceCount =
    overview?.package_devices.filter((device) => device.upgrade_to === null)
      .length ?? 0;
  const shouldShowPackagePlatformNotice =
    !isPlatformAccount && !canAccessPackageManagement;
  const canRequestNetworkRenewal = canManageProjects;
  const resetPackageControls = () => {
    setPackageKeyword("");
    setCurrentPackageFilter("all");
    setTargetPackageFilter("all");
    setPackagePage(1);
    setPackagePageSize(DEFAULT_MODAL_PAGE_SIZE);
  };
  const refreshPackageData = () => {
    void loadDashboard();
  };
  const showRenewalContactNotice = () => {
    message.info(t("home:dashboard.renewal.contactService"));
  };
  const renewalAction = canRequestNetworkRenewal ? (
    <Button
      type="link"
      style={headerLinkButtonStyle}
      onClick={showRenewalContactNotice}
    >
      {t("home:dashboard.actions.renew")}
    </Button>
  ) : null;
  const handleRenew = async () => {
    showRenewalContactNotice();
  };

  const packageOptions = useMemo(
    () =>
      (overview?.package_distribution ?? []).map((item) => ({
        label: packageLabel(item.package_key),
        value: item.package_key,
      })),
    [overview, packageLabel],
  );
  const recognitionTotalDetections =
    recognitionDashboard.trendSummary?.totalDetections;
  const recognitionScopeSummary = useMemo(() => {
    const packageTexts = (overview?.package_distribution ?? [])
      .filter((item) => item.count > 0)
      .map((item) =>
        t("home:dashboard.recognition.packageCount", {
          package: packageLabel(item.package_key),
          count: item.count,
          defaultValue: `${packageLabel(item.package_key)}${item.count}台`,
        }),
      );

    return [
      t("home:dashboard.recognition.deviceCount", {
        count: overview?.total_devices ?? 0,
        defaultValue: `设备总数：${overview?.total_devices ?? 0}`,
      }),
      ...packageTexts,
    ].join(" / ");
  }, [overview, packageLabel, t]);
  const recognitionRangeReasonDefaultLabel =
    recognitionDashboard.range.reason === "firstDeviceCreatedAt"
      ? "从首台设备开始统计"
      : recognitionDashboard.range.reason === "custom"
        ? "手动选择时间范围"
        : "暂无设备统计日期，按当天展示";
  const recognitionRangeReasonLabel = t(
    `home:dashboard.recognition.rangeReasons.${recognitionDashboard.range.reason}`,
    {
      defaultValue: recognitionRangeReasonDefaultLabel,
    },
  );
  const rawRecognitionTrendData =
    recognitionDashboard.trendSummary?.trendData ?? EMPTY_HOME_TREND_DATA;
  const recognitionTrendData = useMemo(
    () =>
      buildHomeTrendSeries(
        recognitionDashboard.range,
        rawRecognitionTrendData,
      ),
    [rawRecognitionTrendData, recognitionDashboard.range],
  );
  const recognitionTrendPoints = useMemo(
    () => buildTrendChartPoints(recognitionTrendData),
    [recognitionTrendData],
  );
  const recognitionTrendMaxCount = useMemo(
    () => getHomeTrendMaxCount(recognitionTrendData),
    [recognitionTrendData],
  );
  const recognitionTrendYAxisTicks = useMemo(() => {
    const innerHeight =
      HOME_TREND_CHART_HEIGHT -
      HOME_TREND_CHART_PADDING_TOP -
      HOME_TREND_CHART_PADDING_BOTTOM;

    return Array.from({ length: HOME_TREND_CHART_Y_TICK_COUNT }, (_, index) => {
      const ratio = index / Math.max(1, HOME_TREND_CHART_Y_TICK_COUNT - 1);
      return {
        value: Math.round(recognitionTrendMaxCount * (1 - ratio)),
        y: HOME_TREND_CHART_PADDING_TOP + innerHeight * ratio,
      };
    });
  }, [recognitionTrendMaxCount]);
  const recognitionTrendXAxisTicks = useMemo(() => {
    if (recognitionTrendPoints.length <= HOME_TREND_CHART_X_LABEL_COUNT) {
      return recognitionTrendPoints;
    }

    const step = Math.ceil(
      (recognitionTrendPoints.length - 1) /
        Math.max(1, HOME_TREND_CHART_X_LABEL_COUNT - 1),
    );

    return recognitionTrendPoints.filter(
      (_, index) =>
        index === 0 ||
        index === recognitionTrendPoints.length - 1 ||
        index % step === 0,
    );
  }, [recognitionTrendPoints]);
  const recognitionTrendVisiblePoints =
    recognitionTrendPoints.length <= 45
      ? recognitionTrendPoints
      : recognitionTrendXAxisTicks;
  const recognitionTrendLinePath = recognitionTrendPoints
    .map((point, index) => `${index === 0 ? "M" : "L"} ${point.x} ${point.y}`)
    .join(" ");
  const recognitionTrendAreaPath =
    recognitionTrendPoints.length > 1
      ? `${recognitionTrendLinePath} L ${
          recognitionTrendPoints[recognitionTrendPoints.length - 1].x
        } ${HOME_TREND_CHART_HEIGHT - HOME_TREND_CHART_PADDING_BOTTOM} L ${
          recognitionTrendPoints[0].x
        } ${HOME_TREND_CHART_HEIGHT - HOME_TREND_CHART_PADDING_BOTTOM} Z`
      : "";
  const recognitionTrendPeak = recognitionTrendData.reduce<
    { date: string; count: number } | null
  >(
    (currentPeak, item) =>
      !currentPeak || item.count > currentPeak.count ? item : currentPeak,
    null,
  );
  const recognitionTrendAverage =
    recognitionTrendData.length > 0
      ? recognitionTrendData.reduce((sum, item) => sum + item.count, 0) /
        recognitionTrendData.length
      : 0;
  const handleRecognitionTrendMouseMove = useCallback(
    (event: React.MouseEvent<SVGSVGElement>) => {
      if (recognitionTrendPoints.length === 0) {
        setRecognitionHoveredPoint(null);
        return;
      }

      const svgRect = event.currentTarget.getBoundingClientRect();
      const pointerX =
        ((event.clientX - svgRect.left) / Math.max(1, svgRect.width)) *
        HOME_TREND_CHART_WIDTH;
      const nearestPoint = recognitionTrendPoints.reduce((nearest, point) =>
        Math.abs(point.x - pointerX) < Math.abs(nearest.x - pointerX)
          ? point
          : nearest,
      );

      setRecognitionHoveredPoint(nearestPoint);
    },
    [recognitionTrendPoints],
  );
  const handleRecognitionTrendMouseLeave = useCallback(() => {
    setRecognitionHoveredPoint(null);
  }, []);

  const networkPagination = useMemo<TablePaginationConfig>(
    () => ({
      current: networkPage,
      pageSize: networkPageSize,
      total: renewalContext?.total ?? 0,
      showSizeChanger: true,
      pageSizeOptions: MODAL_PAGE_SIZE_OPTIONS,
      showTotal: (total) =>
        t("home:dashboard.filters.currentMatched", {
          matched: total,
          total: networkDueDevices.length,
        }),
      onChange: (page, nextPageSize) => {
        const resolvedPageSize = nextPageSize ?? networkPageSize;
        setNetworkPageSize(resolvedPageSize);
        setNetworkPage(resolvedPageSize !== networkPageSize ? 1 : page);
      },
    }),
    [networkDueDevices.length, networkPage, networkPageSize, renewalContext?.total, t],
  );

  const packagePagination = useMemo<TablePaginationConfig>(
    () => ({
      current: packagePage,
      pageSize: packagePageSize,
      total: overview?.total ?? 0,
      showSizeChanger: true,
      pageSizeOptions: MODAL_PAGE_SIZE_OPTIONS,
      showTotal: (total) =>
        t("home:dashboard.filters.currentMatched", {
          matched: total,
          total: overview?.package_devices.length ?? 0,
        }),
      onChange: (page, nextPageSize) => {
        const resolvedPageSize = nextPageSize ?? packagePageSize;
        setPackagePageSize(resolvedPageSize);
        setPackagePage(resolvedPageSize !== packagePageSize ? 1 : page);
      },
    }),
    [overview?.package_devices.length, overview?.total, packagePage, packagePageSize, t],
  );

  const handleRenewalKeywordChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRenewalKeyword(event.target.value);
    setNetworkPage(1);
  };

  const handleRenewalStatusFilterChange = (
    value: HomeRenewalStatus | "all",
  ) => {
    setRenewalStatusFilter(value);
    setNetworkPage(1);
  };

  const handlePackageKeywordChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setPackageKeyword(event.target.value);
    setPackagePage(1);
  };

  const handleCurrentPackageFilterChange = (value: string) => {
    setCurrentPackageFilter(value);
    setPackagePage(1);
  };

  const handleTargetPackageFilterChange = (value: string) => {
    setTargetPackageFilter(value);
    setPackagePage(1);
  };

  const renderPreviewFooter = (
    total: number,
    previewCount: number,
    withDeviceSuffix = false,
  ) => (
    <Text type="secondary">
      {total > previewCount
        ? t(
            withDeviceSuffix
              ? "home:dashboard.preview.moreDevices"
              : "home:dashboard.preview.more",
            {
              count: total - previewCount,
            },
          )
        : t("home:dashboard.preview.all")}
    </Text>
  );

  const renderCardHeader = (
    title: React.ReactNode,
    actions: React.ReactNode,
    badge?: React.ReactNode,
  ) => (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 8,
        width: "100%",
      }}
    >
      <div
        style={{
          alignItems: "center",
          display: "flex",
          gap: 8,
          justifyContent: "space-between",
          width: "100%",
        }}
      >
        <Text strong style={{ whiteSpace: "nowrap" }}>
          {title}
        </Text>
        <Space size={12} style={{ flexShrink: 0 }}>
          {actions}
        </Space>
      </div>
      {badge ? (
        <div style={cardHeaderBadgeRowStyle}>
          {badge}
        </div>
      ) : null}
    </div>
  );

  const networkColumns: TableColumnsType<HomeRenewalDevice> = [
    {
      title: t("home:dashboard.tables.deviceName"),
      dataIndex: "name",
      key: "name",
      width: "20%",
      render: (value: string) => (
        <Text strong style={tableTextWrapStyle}>
          {value}
        </Text>
      ),
    },
    {
      title: t("home:dashboard.tables.project"),
      dataIndex: "project_name",
      key: "project_name",
      width: "15%",
      render: (value: string) => <span style={tableTextWrapStyle}>{value}</span>,
    },
    {
      title: t("home:dashboard.tables.currentPackage"),
      dataIndex: "package_key",
      key: "package_key",
      width: "14%",
      render: (packageKey: string) => (
        <Tag color={getPackageTagColor(packageKey)}>{packageLabel(packageKey)}</Tag>
      ),
    },
    {
      title: t("home:dashboard.tables.dueAt"),
      dataIndex: "due_at",
      key: "due_at",
      width: "13%",
      render: (value: string) => formatDateOnly(value),
    },
    {
      title: t("home:dashboard.tables.status"),
      dataIndex: "status",
      key: "status",
      width: "12%",
      render: (status: HomeRenewalStatus) => (
        <Tag color={getRenewalStatusColor(status)}>
          {t(`home:dashboard.renewalStatus.${status}`)}
        </Tag>
      ),
    },
    {
      title: t("home:dashboard.tables.lastOnlineAt"),
      dataIndex: "last_online_at",
      key: "last_online_at",
      width: "14%",
      render: (value: string | null) => formatDateTime(value),
    },
  ];

  const packageColumns: TableColumnsType<HomePackageDevice> = [
    {
      title: t("home:dashboard.tables.deviceName"),
      dataIndex: "name",
      key: "name",
      width: "24%",
      render: (value: string) => (
        <Text strong style={tableTextWrapStyle}>
          {value}
        </Text>
      ),
    },
    {
      title: t("home:dashboard.tables.deviceCode"),
      dataIndex: "device_code",
      key: "device_code",
      width: "18%",
      render: (value: string) => <span style={tableTextWrapStyle}>{value}</span>,
    },
    {
      title: t("home:dashboard.tables.project"),
      dataIndex: "project_name",
      key: "project_name",
      width: "22%",
      render: (value: string) => <span style={tableTextWrapStyle}>{value}</span>,
    },
    {
      title: t("home:dashboard.tables.currentPackage"),
      dataIndex: "package_key",
      key: "package_key",
      width: "18%",
      render: (packageKey: string) => (
        <Tag color={getPackageTagColor(packageKey)}>{packageLabel(packageKey)}</Tag>
      ),
    },
    {
      title: t("home:dashboard.tables.upgradeTo"),
      dataIndex: "upgrade_to",
      key: "upgrade_to",
      width: "18%",
      render: (packageKey: string | null) =>
        packageKey ? (
          <Tag color={getPackageTagColor(packageKey)}>{packageLabel(packageKey)}</Tag>
        ) : (
          <Text type="secondary">{t("home:dashboard.package.highest")}</Text>
        ),
    },
  ];

  if (!overview) {
    return (
      <div className="home-container">
        <Card loading />
      </div>
    );
  }

  const offlinePreview = overview.offline_devices.slice(0, PREVIEW_DEVICE_COUNT);
  const lurePreview = overview.lure_reminders.slice(0, PREVIEW_DEVICE_COUNT);
  const networkPreview = networkDueDevices.slice(0, PREVIEW_DEVICE_COUNT);
  const packagePreview = overview.package_devices.slice(0, PACKAGE_PREVIEW_COUNT);
  const lureTotal = overview.alerts.lure_warning + overview.alerts.lure_expired;
  const networkTotal =
    overview.alerts.network_expiring + overview.alerts.network_overdue;
  const lureExpiredTotal = overview.alerts.lure_expired;
  const networkOverdueTotal = overview.alerts.network_overdue;
  const summaryCards = [
    {
      key: "offline" as const,
      title: t("home:dashboard.alerts.offline"),
      value: overview.alerts.offline_devices,
      icon: <DisconnectOutlined />,
    },
    {
      key: "lure" as const,
      title: t("home:dashboard.alerts.lure"),
      value: lureExpiredTotal,
      icon: <BellOutlined />,
    },
    {
      key: "network" as const,
      title: t("home:dashboard.alerts.networkDue"),
      value: networkOverdueTotal,
      icon: <CloudServerOutlined />,
    },
  ];
  const systemFeatureCards = [
    {
      key: "efficient",
      icon: <ThunderboltOutlined />,
      title: t("home:features.efficient.title"),
      description: t("home:features.efficient.description"),
    },
    {
      key: "accurate",
      icon: <SafetyCertificateOutlined />,
      title: t("home:features.accurate.title"),
      description: t("home:features.accurate.description"),
    },
    {
      key: "comprehensive",
      icon: <BarChartOutlined />,
      title: t("home:features.comprehensive.title"),
      description: t("home:features.comprehensive.description"),
    },
  ];

  const renderNetworkModalContent = (
    tableScrollY = RENEWAL_MODAL_TABLE_SCROLL_Y,
  ) => (
    <Space direction="vertical" size={16} style={{ width: "100%" }}>
      <Text type="secondary">
        {t("home:dashboard.network.scopeNotice", {
          project: localizedCurrentProjectName,
          scope: localizedCurrentScopeName,
        })}
      </Text>
      <div
        data-testid="homepage-detail-filter-toolbar"
        style={detailFilterToolbarStyle}
      >
        <div style={detailFilterSearchStyle}>
          <Input
            value={renewalKeyword}
            onChange={handleRenewalKeywordChange}
            placeholder={t("home:dashboard.filters.networkKeyword")}
            style={{ width: "100%" }}
          />
        </div>
        <div style={detailFilterSelectStyle}>
          <Select
            style={{ width: "100%" }}
            value={renewalStatusFilter}
            onChange={handleRenewalStatusFilterChange}
            options={[
              {
                label: t("home:dashboard.filters.allStatuses"),
                value: "all",
              },
              {
                label: t("home:dashboard.renewalStatus.expiring"),
                value: "expiring",
              },
              {
                label: t("home:dashboard.renewalStatus.overdue"),
                value: "overdue",
              },
            ]}
          />
        </div>
        <div
          data-testid="homepage-detail-filter-actions"
          style={detailFilterActionsStyle}
        >
          <Button onClick={resetRenewalControls}>
            {t("common:buttons.reset")}
          </Button>
          <Button icon={<ReloadOutlined />} onClick={refreshRenewalData}>
            {t("common:buttons.refresh")}
          </Button>
        </div>
      </div>
      <Text type="secondary">
        {t("home:dashboard.filters.currentMatched", {
          matched: renewalContext.total,
          total: networkDueDevices.length,
        })}
      </Text>
      {renewalOpen ? (
        <Row gutter={[12, 12]}>
          <Col xs={24} sm={12} lg={6}>
            <Card size="small">
              <Text type="secondary">{t("home:dashboard.renewal.expiring")}</Text>
              <div style={{ marginTop: 4 }}>
                <Text strong>{renewalContext.expiring_count}</Text>
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card size="small">
              <Text type="secondary">{t("home:dashboard.renewal.overdue")}</Text>
              <div style={{ marginTop: 4 }}>
                <Text strong>{renewalContext.overdue_count}</Text>
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card size="small">
              <Text type="secondary">{t("home:dashboard.renewal.renewable")}</Text>
              <div style={{ marginTop: 4 }}>
                <Text strong>
                  {networkDueDevices.filter((device) => device.can_renew).length}
                </Text>
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card size="small">
              <Text type="secondary">{t("home:dashboard.renewal.selectedDevices")}</Text>
              <div style={{ marginTop: 4 }}>
                <Text strong>{selectedRenewalDeviceIds.length}</Text>
              </div>
            </Card>
          </Col>
        </Row>
      ) : null}
      {renewalOpen ? (
        <Alert
          type="info"
          showIcon
          message={t("home:dashboard.renewal.renewRule")}
        />
      ) : null}
      <Table
        rowKey="id"
        size="middle"
        columns={networkColumns}
        dataSource={renewalFilteredDevices}
        rowSelection={
          renewalOpen
            ? {
                selectedRowKeys: selectedRenewalDeviceIds,
                onChange: (selectedRowKeys) =>
                  setSelectedRenewalDeviceIds(selectedRowKeys.map(String)),
                getCheckboxProps: (device) => ({
                  disabled: !device.can_renew,
                }),
              }
            : undefined
        }
        pagination={networkPagination}
        tableLayout="fixed"
        scroll={{ y: tableScrollY }}
      />
    </Space>
  );

  return (
    <div className="home-container">
      <Space direction="vertical" size={24} style={{ width: "100%" }}>
        <section className="home-recognition-dashboard">
          <div className="home-recognition-total-panel">
            <div className="home-recognition-total-main">
              <Text className="home-recognition-kicker">
                {t("home:dashboard.recognition.coreOverview", {
                  defaultValue: "核心概况",
                })}
              </Text>
              <Text strong className="home-recognition-total-label">
                {t("home:dashboard.recognition.totalKilled", {
                  defaultValue: "灭杀总数",
                })}
              </Text>
              <div
                className={`home-recognition-total-value${
                  recognitionDashboard.loading ? " is-loading" : ""
                }`}
              >
                {recognitionDashboard.loading
                  ? "0"
                  : formatDashboardNumber(recognitionTotalDetections)}
              </div>
            </div>
            <Text className="home-recognition-scope">
              {recognitionScopeSummary}
            </Text>
          </div>

          <div className="home-recognition-chart-panel">
            <div className="home-recognition-chart-header">
              <div>
                <Text strong className="home-recognition-chart-title">
                  {t("home:dashboard.recognition.trendTitle", {
                    defaultValue: "识别概况",
                  })}
                </Text>
                <Text type="secondary" className="home-recognition-chart-subtitle">
                  {t("home:dashboard.recognition.trendSubtitle", {
                    defaultValue: "按时间范围查看识别总数变化",
                  })}
                </Text>
              </div>
              <RangePicker
                allowClear={false}
                className="home-recognition-range"
                format="YYYY-MM-DD"
                suffixIcon={
                  <CalendarOutlined className="home-recognition-range-icon" />
                }
                title={recognitionRangeReasonLabel}
                value={[
                  recognitionDashboard.range.start,
                  recognitionDashboard.range.end,
                ]}
                onChange={(dates) =>
                  handleRecognitionRangeChange(
                    dates as null | [Dayjs | null, Dayjs | null],
                  )
                }
              />
            </div>

            <div className="home-recognition-metrics">
              <div>
                <Text type="secondary">
                  {t("home:dashboard.recognition.periodTotal", {
                    defaultValue: "周期识别数",
                  })}
                </Text>
                <strong>
                  {recognitionDashboard.loading
                    ? "-"
                    : formatDashboardNumber(
                        recognitionDashboard.trendSummary?.totalDetections,
                      )}
                </strong>
              </div>
              <div>
                <Text type="secondary">
                  {t("home:dashboard.recognition.averagePerDay", {
                    defaultValue: "日均识别",
                  })}
                </Text>
                <strong>
                  {recognitionDashboard.loading
                    ? "-"
                    : recognitionTrendAverage.toFixed(1)}
                </strong>
              </div>
              <div>
                <Text type="secondary">
                  {t("home:dashboard.recognition.peakCount", {
                    defaultValue: "峰值数量",
                  })}
                </Text>
                <strong>
                  {recognitionDashboard.loading
                    ? "-"
                    : formatDashboardNumber(recognitionTrendPeak?.count)}
                </strong>
              </div>
              <div>
                <Text type="secondary">
                  {t("home:dashboard.recognition.peakDate", {
                    defaultValue: "峰值日期",
                  })}
                </Text>
                <strong>{recognitionTrendPeak?.date ?? "-"}</strong>
              </div>
            </div>

            <div className="home-recognition-chart-body">
              {recognitionDashboard.loading ? (
                <Spin />
              ) : (
                <div className="home-recognition-chart-plot">
                  <svg
                    viewBox={`0 0 ${HOME_TREND_CHART_WIDTH} ${HOME_TREND_CHART_HEIGHT}`}
                    role="img"
                    aria-label={t("home:dashboard.recognition.trendTitle", {
                      defaultValue: "识别概况",
                    })}
                    preserveAspectRatio="none"
                    onMouseMove={handleRecognitionTrendMouseMove}
                    onMouseLeave={handleRecognitionTrendMouseLeave}
                  >
                    {recognitionTrendYAxisTicks.map((tick) => (
                      <g key={`y-${tick.value}-${tick.y}`}>
                        <text
                          x={HOME_TREND_CHART_PADDING_LEFT - 8}
                          y={tick.y + 4}
                          textAnchor="end"
                          className="home-recognition-axis-label"
                        >
                          {tick.value}
                        </text>
                        <line
                          x1={HOME_TREND_CHART_PADDING_LEFT}
                          x2={
                            HOME_TREND_CHART_WIDTH -
                            HOME_TREND_CHART_PADDING_RIGHT
                          }
                          y1={tick.y}
                          y2={tick.y}
                          className="home-recognition-grid-line"
                        />
                      </g>
                    ))}
                    {recognitionTrendXAxisTicks.map((point) => (
                      <g key={`x-${point.date}-${point.x}`}>
                        <line
                          x1={point.x}
                          x2={point.x}
                          y1={HOME_TREND_CHART_PADDING_TOP}
                          y2={
                            HOME_TREND_CHART_HEIGHT -
                            HOME_TREND_CHART_PADDING_BOTTOM
                          }
                          className="home-recognition-grid-line"
                        />
                        <text
                          x={point.x}
                          y={HOME_TREND_CHART_HEIGHT - 12}
                          textAnchor="middle"
                          className="home-recognition-axis-label"
                        >
                          {formatHomeTrendAxisDate(point.date)}
                        </text>
                      </g>
                    ))}
                    <line
                      x1={HOME_TREND_CHART_PADDING_LEFT}
                      x2={HOME_TREND_CHART_WIDTH - HOME_TREND_CHART_PADDING_RIGHT}
                      y1={HOME_TREND_CHART_HEIGHT - HOME_TREND_CHART_PADDING_BOTTOM}
                      y2={HOME_TREND_CHART_HEIGHT - HOME_TREND_CHART_PADDING_BOTTOM}
                      className="home-recognition-axis-line"
                    />
                    {recognitionHoveredPoint && (
                      <line
                        x1={recognitionHoveredPoint.x}
                        x2={recognitionHoveredPoint.x}
                        y1={HOME_TREND_CHART_PADDING_TOP}
                        y2={
                          HOME_TREND_CHART_HEIGHT -
                          HOME_TREND_CHART_PADDING_BOTTOM
                        }
                        className="home-recognition-hover-line"
                      />
                    )}
                    {recognitionTrendAreaPath && (
                      <path
                        d={recognitionTrendAreaPath}
                        className="home-recognition-area-path"
                      />
                    )}
                    <path
                      d={recognitionTrendLinePath}
                      className="home-recognition-line-path"
                    />
                    {recognitionTrendVisiblePoints.map((point) => (
                      <circle
                        key={`${point.date}-${point.x}`}
                        cx={point.x}
                        cy={point.y}
                        r="4"
                        className="home-recognition-point"
                      />
                    ))}
                    {recognitionHoveredPoint && (
                      <circle
                        cx={recognitionHoveredPoint.x}
                        cy={recognitionHoveredPoint.y}
                        r="5"
                        className="home-recognition-point is-active"
                      />
                    )}
                    <rect
                      x="0"
                      y="0"
                      width={HOME_TREND_CHART_WIDTH}
                      height={HOME_TREND_CHART_HEIGHT}
                      className="home-recognition-hover-layer"
                    />
                  </svg>
                  {recognitionHoveredPoint && (
                    <div
                      className="home-recognition-tooltip"
                      style={{
                        left: `${(recognitionHoveredPoint.x / HOME_TREND_CHART_WIDTH) * 100}%`,
                        top: `${(recognitionHoveredPoint.y / HOME_TREND_CHART_HEIGHT) * 100}%`,
                      }}
                    >
                      <div>{recognitionHoveredPoint.date}</div>
                      <strong>
                        {t("home:dashboard.recognition.tooltipTotal", {
                          count: recognitionHoveredPoint.count,
                          defaultValue: `总量：${recognitionHoveredPoint.count}`,
                        })}
                      </strong>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </section>

        {/* TODO(v1.9-cleanup): these legacy overview blocks are hidden while the
            current homepage focuses on the new recognition overview. Keep the
            markup temporarily as a cleanup marker only; the homepage no longer
            requests its renewal detail API by default. */}
        <div hidden style={summaryShellStyle}>
          <Space direction="vertical" size={16} style={{ width: "100%" }}>
            <Text strong>{t("home:dashboard.overview.title")}</Text>
            <Row gutter={[16, 16]} align="stretch">
              <Col xs={24} lg={6}>
                <div style={{ ...serviceOverviewStyle, height: "100%" }}>
                  <Space direction="vertical" size={8}>
                    <Text type="secondary">
                      {t("home:dashboard.overview.currentScope", {
                        scope: localizedCurrentScopeName,
                      })}
                    </Text>
                    <Text strong style={{ fontSize: 28, lineHeight: 1 }}>
                      {overview.total_devices}
                    </Text>
                    <Text type="secondary">{t("home:dashboard.units.devices")}</Text>
                  </Space>
                </div>
              </Col>
              <Col xs={24} lg={6}>
                <div style={{ ...serviceOverviewStyle, height: "100%" }}>
                  <Space direction="vertical" size={10} style={{ width: "100%" }}>
                    <Text strong>{t("home:dashboard.overview.packageDistribution")}</Text>
                    <Space wrap size={[8, 8]}>
                      {overview.package_distribution.map((item) => (
                        <Tag
                          key={item.package_key}
                          color={getPackageTagColor(item.package_key)}
                        >
                          {t("home:dashboard.overview.packageDistributionItem", {
                            package: packageLabel(item.package_key),
                            count: item.count,
                          })}
                        </Tag>
                      ))}
                    </Space>
                  </Space>
                </div>
              </Col>
              {summaryCards.map((item) => (
                <Col xs={24} md={8} lg={4} key={item.key}>
                  <div
                    style={{
                      ...summaryPanelBaseStyle,
                      ...summaryPanelStyles[item.key],
                      height: "100%",
                    }}
                  >
                    <Space direction="vertical" size={12}>
                      <Text strong style={summaryValueStyles[item.key]}>
                        <Space size={8}>
                          {item.icon}
                          {item.title}
                        </Space>
                      </Text>
                      <Text
                        strong
                        style={{
                          ...summaryValueStyles[item.key],
                          fontSize: 32,
                          lineHeight: 1,
                        }}
                      >
                        {item.value}
                      </Text>
                    </Space>
                  </div>
                </Col>
              ))}
            </Row>
          </Space>
        </div>

        {/* TODO(v1.9-cleanup): remove hidden offline/lure/network/package cards
            with the related homepage detail modal code after product confirms
            those old entry points are no longer needed. */}
        <div hidden>
          <Row gutter={[22, 22]} align="stretch">
            <Col xs={24} lg={12} xl={6}>
              <Card
                title={renderCardHeader(
                  t("home:dashboard.offline.title"),
                  canReadDevices ? (
                    <Button
                      type="link"
                      style={headerLinkButtonStyle}
                      onClick={() => navigate("/device-management?status=offline")}
                    >
                      {t("home:dashboard.actions.viewAll")}
                    </Button>
                  ) : null,
                  <Tag color="orange">
                    {t("home:dashboard.offline.countBadge", {
                      count: overview.alerts.offline_devices,
                    })}
                  </Tag>,
                )}
                style={dashboardCardStyle}
                styles={{ body: dashboardCardBodyStyle }}
              >
                {offlinePreview.length === 0 ? (
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                ) : (
                  <Space direction="vertical" size={0} style={{ width: "100%" }}>
                    <div style={listPreviewStyle}>
                      {offlinePreview.map((device) => (
                        <div key={device.id} style={cardListItemStyle}>
                          <Text strong>{device.name}</Text>
                          <div style={{ marginTop: 6 }}>
                            <Text type="secondary">
                              {t("home:dashboard.offline.lastOnline", {
                                time: formatDateTime(device.last_online_at),
                              })}
                            </Text>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div style={{ paddingTop: 12 }}>
                      {renderPreviewFooter(
                        overview.alerts.offline_devices,
                        offlinePreview.length,
                      )}
                    </div>
                  </Space>
                )}
              </Card>
            </Col>

            <Col xs={24} lg={12} xl={6}>
              <Card
                title={renderCardHeader(
                  t("home:dashboard.lure.title"),
                  canReadDevices ? (
                    <Button
                      type="link"
                      style={headerLinkButtonStyle}
                      onClick={() =>
                        navigate("/device-management?lure_filter=expiring_7_days")
                      }
                    >
                      {t("home:dashboard.actions.viewAll")}
                    </Button>
                  ) : null,
                  <Space wrap size={4}>
                    <Tag color="orange">
                      {t("home:dashboard.lure.warningCount", {
                        count: overview.alerts.lure_warning,
                      })}
                    </Tag>
                    <Tag color="red">
                      {t("home:dashboard.lure.expiredCount", {
                        count: overview.alerts.lure_expired,
                      })}
                    </Tag>
                  </Space>,
                )}
                style={dashboardCardStyle}
                styles={{ body: dashboardCardBodyStyle }}
              >
                {lurePreview.length === 0 ? (
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                ) : (
                  <Space direction="vertical" size={12} style={{ width: "100%" }}>
                    <div style={listPreviewStyle}>
                      {lurePreview.map((device: HomeLureReminder) => (
                        <div key={device.id} style={cardListItemStyle}>
                          <Space wrap size={8}>
                            <Text strong>{device.name}</Text>
                            <Tag color={device.status === "expired" ? "red" : "orange"}>
                              {t(`home:dashboard.lureStatus.${device.status}`)}
                            </Tag>
                          </Space>
                          <div style={{ marginTop: 6 }}>
                            <Space wrap size={8}>
                              <Text type="secondary">
                                {device.days_remaining >= 0
                                  ? t("home:dashboard.lure.daysRemaining", {
                                      count: device.days_remaining,
                                    })
                                  : t("home:dashboard.lure.daysOverdue", {
                                      count: Math.abs(device.days_remaining),
                                    })}
                              </Text>
                              <Text type="secondary">
                                {t("home:dashboard.lure.dueAtLabel", {
                                  time: formatDateOnly(device.due_at),
                                })}
                              </Text>
                            </Space>
                          </div>
                        </div>
                      ))}
                    </div>
                    {renderPreviewFooter(lureTotal, lurePreview.length)}
                  </Space>
                )}
              </Card>
            </Col>

            <Col xs={24} lg={12} xl={6}>
              <Card
                title={renderCardHeader(
                  t("home:dashboard.network.title"),
                  <>
                    <Button
                      type="link"
                      style={headerLinkButtonStyle}
                      onClick={() => setNetworkDetailOpen(true)}
                    >
                      {t("home:dashboard.actions.viewAll")}
                    </Button>
                    {renewalAction}
                  </>,
                  <Space wrap size={4}>
                    <Tag color="orange">
                      {t("home:dashboard.network.expiringCount", {
                        count: overview.alerts.network_expiring,
                      })}
                    </Tag>
                    <Tag color="red">
                      {t("home:dashboard.network.overdueCount", {
                        count: overview.alerts.network_overdue,
                      })}
                    </Tag>
                  </Space>,
                )}
                style={dashboardCardStyle}
                styles={{ body: dashboardCardBodyStyle }}
              >
                <Space direction="vertical" size={12} style={{ width: "100%" }}>
                  {networkPreview.length === 0 ? (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                  ) : (
                    <>
                      <div style={listPreviewStyle}>
                        {networkPreview.map((device) => (
                          <div key={device.id} style={cardListItemStyle}>
                            <Space wrap size={8}>
                              <Text strong>{device.name}</Text>
                              <Tag color={getRenewalStatusColor(device.status)}>
                                {t(`home:dashboard.renewalStatus.${device.status}`)}
                              </Tag>
                            </Space>
                            <div style={{ marginTop: 6 }}>
                              <Space direction="vertical" size={2}>
                                <Text type="secondary">
                                  {t("home:dashboard.network.dueAtLabel", {
                                    time: formatDateOnly(device.due_at),
                                  })}
                                </Text>
                                <Text type="secondary">
                                  {t("home:dashboard.network.cloudFee", {
                                    fee: formatCurrency(device.cloud_platform_fee),
                                  })}
                                </Text>
                              </Space>
                            </div>
                          </div>
                        ))}
                      </div>
                      {renderPreviewFooter(networkTotal, networkPreview.length, true)}
                    </>
                  )}
                </Space>
              </Card>
            </Col>

            <Col xs={24} lg={12} xl={6}>
              <Card
                title={renderCardHeader(
                  t("home:dashboard.package.title"),
                  <>
                    <Button
                      type="link"
                      style={headerLinkButtonStyle}
                      onClick={() => setPackageDetailOpen(true)}
                    >
                      {t("home:dashboard.actions.viewAll")}
                    </Button>
                  </>,
                  <Space wrap>
                    <Tag color="blue">
                      {t("home:dashboard.package.upgradable", {
                        count: upgradableDeviceCount,
                      })}
                    </Tag>
                    <Tag color="default">
                      {t("home:dashboard.package.highestCount", {
                        count: highestPackageDeviceCount,
                      })}
                    </Tag>
                  </Space>,
                )}
                style={dashboardCardStyle}
                styles={{ body: dashboardCardBodyStyle }}
              >
                <Space direction="vertical" size={12} style={{ width: "100%" }}>
                  {shouldShowPackagePlatformNotice ? (
                    <Text type="secondary">{t("home:dashboard.package.readonly")}</Text>
                  ) : null}
                  {packagePreview.length === 0 ? (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                  ) : (
                    <>
                      <div style={listPreviewStyle}>
                        {packagePreview.map((device) => (
                          <div key={device.id} style={cardListItemStyle}>
                            <Space wrap size={8}>
                              <Text strong>{device.name}</Text>
                              <Tag color={getPackageTagColor(device.package_key)}>
                                {packageLabel(device.package_key)}
                              </Tag>
                            </Space>
                            <div style={{ marginTop: 6 }}>
                              {device.upgrade_to ? (
                                <Tag color={getPackageTagColor(device.upgrade_to)}>
                                  {t("home:dashboard.package.suggestUpgrade", {
                                    package: packageLabel(device.upgrade_to),
                                  })}
                                </Tag>
                              ) : (
                                <Tag>{t("home:dashboard.package.highest")}</Tag>
                              )}
                            </div>
                            <div style={{ marginTop: 6 }}>
                              <Text type="secondary">
                                {t("home:dashboard.package.project", {
                                  project: device.project_name,
                                })}
                              </Text>
                            </div>
                          </div>
                        ))}
                      </div>
                      {renderPreviewFooter(
                        overview.package_devices.length,
                        packagePreview.length,
                        true,
                      )}
                    </>
                  )}
                </Space>
              </Card>
            </Col>
          </Row>
        </div>
      </Space>

      <Modal
        title={t("home:dashboard.network.detailTitle")}
        open={networkDetailOpen}
        onCancel={() => setNetworkDetailOpen(false)}
        maskClosable={false}
        footer={null}
        width={wideModalWidth}
        styles={{ body: detailModalBodyStyle }}
      >
        {renderNetworkModalContent(DETAIL_MODAL_TABLE_SCROLL_Y)}
      </Modal>

      <Modal
        title={t("home:dashboard.renewal.title")}
        open={renewalOpen}
        onCancel={closeRenewalModal}
        onOk={() => void handleRenew()}
        okText={t("home:dashboard.actions.confirmRenew")}
        cancelText={t("common:buttons.cancel")}
        okButtonProps={{
          disabled:
            renewalFilteredDevices.length === 0 ||
            selectedRenewalDeviceIds.length === 0,
        }}
        maskClosable={false}
        width={wideModalWidth}
        styles={{ body: modalBodyStyle }}
      >
        {renderNetworkModalContent()}
      </Modal>

      <Modal
        title={t("home:dashboard.package.detailTitle")}
        open={packageDetailOpen}
        onCancel={() => setPackageDetailOpen(false)}
        maskClosable={false}
        footer={null}
        width={1220}
        styles={{ body: detailModalBodyStyle }}
      >
        <Space direction="vertical" size={16} style={{ width: "100%" }}>
          {shouldShowPackagePlatformNotice ? (
            <Alert
              type="info"
              showIcon
              icon={<InfoCircleOutlined />}
              message={t("home:dashboard.package.readonly")}
            />
          ) : null}
          <div
            data-testid="homepage-detail-filter-toolbar"
            style={detailFilterToolbarStyle}
          >
            <div style={detailFilterSearchStyle}>
              <Input
                value={packageKeyword}
                onChange={handlePackageKeywordChange}
                placeholder={t("home:dashboard.filters.packageKeyword")}
                style={{ width: "100%" }}
              />
            </div>
            <div style={detailFilterSelectStyle}>
              <Select
                style={{ width: "100%" }}
                value={currentPackageFilter}
                onChange={handleCurrentPackageFilterChange}
                options={[
                  {
                    label: t("home:dashboard.filters.allCurrentPackages"),
                    value: "all",
                  },
                  ...packageOptions,
                ]}
              />
            </div>
            <div style={detailFilterSelectStyle}>
              <Select
                style={{ width: "100%" }}
                value={targetPackageFilter}
                onChange={handleTargetPackageFilterChange}
                options={[
                  {
                    label: t("home:dashboard.filters.allTargetPackages"),
                    value: "all",
                  },
                  ...packageOptions,
                  {
                    label: t("home:dashboard.package.highest"),
                    value: "none",
                  },
                ]}
              />
            </div>
            <div
              data-testid="homepage-detail-filter-actions"
              style={detailFilterActionsStyle}
            >
              <Button onClick={resetPackageControls}>
                {t("common:buttons.reset")}
              </Button>
              <Button icon={<ReloadOutlined />} onClick={refreshPackageData}>
                {t("common:buttons.refresh")}
              </Button>
            </div>
          </div>
          <Text type="secondary">
            {t("home:dashboard.filters.currentMatched", {
              matched: overview.total,
              total: overview.package_devices.length,
            })}
          </Text>
          <Table
            rowKey="id"
            size="middle"
            pagination={packagePagination}
            columns={packageColumns}
            dataSource={packageDetailDevices}
            tableLayout="fixed"
            scroll={{ y: DETAIL_MODAL_TABLE_SCROLL_Y }}
          />
        </Space>
      </Modal>

      <div hidden style={{ marginTop: 24 }}>
        <Space direction="vertical" size={16} style={{ width: "100%" }}>
          <Title level={4} style={{ marginBottom: 0 }}>
            {t("home:features.sectionTitle")}
          </Title>
          <Row gutter={[16, 16]}>
            {systemFeatureCards.map((feature) => (
              <Col xs={24} md={8} key={feature.key}>
                <Card
                  style={dashboardCardStyle}
                  styles={{ body: { minHeight: 150 } }}
                >
                  <Space direction="vertical" size={10}>
                    <Text strong>
                      <Space size={8}>
                        {feature.icon}
                        {feature.title}
                      </Space>
                    </Text>
                    <Paragraph type="secondary" style={{ marginBottom: 0 }}>
                      {feature.description}
                    </Paragraph>
                  </Space>
                </Card>
              </Col>
            ))}
          </Row>
        </Space>
      </div>
    </div>
  );
};

export default FeatureAwareHome;
